import React, { useState } from "react";
import AnimatedContent from "../common/AnimatedContent";
import PageTitle from "../common/PageTitle";
import { FaFilter } from "react-icons/fa";
import { FaRegCalendarCheck } from "react-icons/fa6";
import { RiAlarmLine } from "react-icons/ri";
import { BiCalendarEvent } from "react-icons/bi";
import { BsEye } from "react-icons/bs";
import Drawer from "../common/Drawer";
import CustomChip from "../common/CustomChip";
import useTasks from "../hooks/useTasks";
import TaskTable from "../Components/task/TaskTable";
import TaskFilter from "../Components/task/TaskFilter";
import TaskInfoTabs from "../Components/task/TaskInfoTabs";
import TaskStatus from "../Enum/TaskStatus";

const remainderLabel = (value) => {
  if (value === "DAILY") return "Daily";
  if (value === "ONE_TIME") return "One Time";
  return "-";
};

const TaskList = () => {
  const [visible, setVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [defaultTab, setDefaultTab] = useState("info");

  const {
    tasks,
    loading,
    limit,
    currentPage,
    nextId,
    prevId,
    hasMore,
    handlePageChange,
    handlePageSize,

    filters,
    handleApplyFilters,
    handleClearFilters,
    removeFilter,
    modalOpen,
    setModalOpen,

    presonList,
  } = useTasks();

  const columns = [
    {
      key: "title",
      title: "Title",
      render: (text, record) => (
        <div
          onClick={() => {
            setSelectedTask(record);
            setDefaultTab("info");
            setVisible(true);
          }}
          title={text}
          className="cursor-pointer text-center truncate"
        >
          {text?.trim().replace(/\b\w/g, (char) => char.toUpperCase())}
        </div>
      ),
    },
    {
      key: "taskDate",
      title: "Task Date",
      render: (text) => (
        <div className="bg-green-50 mx-auto flex items-center justify-center gap-2 text-xs border border-green-500 rounded-full p-1 w-max text-center">
          <FaRegCalendarCheck className="text-primary" />
          <div>{text}</div>
        </div>
      ),
    },
    {
      key: "createdDate",
      title: "Created Date",
      render: (text) => (
        <div className="bg-blue-50 mx-auto flex items-center justify-center gap-2 text-xs border border-blue-500 rounded-full p-1 w-max text-center">
          <BiCalendarEvent className="text-blue-500" />
          <div>{text}</div>
        </div>
      ),
    },
    {
      key: "remainder",
      title: "Reminder",
      render: (text) => (
        <div className="bg-violet-50 mx-auto flex items-center justify-center gap-2 text-xs border border-violet-500 rounded-full p-1 w-max text-center">
          <RiAlarmLine className="text-violet-500" />
          <div>{remainderLabel(text)}</div>
        </div>
      ),
    },
    {
      key: "status",
      title: "Task Status",
      render: (text) => {
        const matchStatus = TaskStatus.find((list) => list.value === text);
        return (
          <div
            className={`text-xs px-3 py-1 border rounded-full w-max mx-auto font-semibold ${
              matchStatus?.value === "COMPLETED"
                ? "border-green-500 bg-green-50 text-green-700"
                : matchStatus?.value === "CANCELLED"
                ? "border-red-500 bg-red-50 text-red-700"
                : matchStatus?.value === "IN_PROGRESS"
                ? "border-blue-500 bg-blue-50 text-blue-700"
                : "bg-yellow-50 border-yellow-500 text-yellow-700"
            }`}
          >
            {matchStatus?.label || text}
          </div>
        );
      },
    },
    {
      key: "assignee",
      title: "Responsible Person",
      render: (text) => <div className="text-center">{text?.name}</div>,
    },
    {
      key: "createdBy",
      title: "Created By",
      render: (text) => <div className="text-center">{text}</div>,
    },
    {
      key: "initialTaskInformationMedium",
      title: "Initial Info Medium",
      width: "140px",
      render: (text) => (
        <div title={text || "-"} className="text-center line-clamp-3 break-words max-w-[140px] mx-auto overflow-hidden">{text || "-"}</div>
      ),
    },
    {
      key: "finalTaskInformationMedium",
      title: "Final Info Medium",
      width: "140px",
      render: (text) => (
        <div title={text || "-"} className="text-center line-clamp-3 break-words max-w-[140px] mx-auto overflow-hidden">{text || "-"}</div>
      ),
    },
    {
      key: "lastModified",
      title: "Last Modified",
      render: (text) => <div className="text-center">{text}</div>,
    },
    {
      key: "Action",
      title: "Action",
      render: (_, record) => (
        <div className="flex justify-center items-center gap-4 text-blue-500 text-lg font-semibold">
          <BsEye
            className="cursor-pointer"
            onClick={() => {
              setSelectedTask(record);
              setDefaultTab("reminders");
              setVisible(true);
            }}
            aria-label="View reminder history"
          />
        </div>
      ),
    },
  ];

  const getFilterLabel = (key, value) => {
    if (key === "assistant" || key === "status" || key === "initialInfoMedium" || key === "finalInfoMedium" || key === "activeStatus") {
      return value?.label || value;
    }
    if (key === "startDate" || key === "endDate") {
      return `${key === "startDate" ? "Start" : "End"}: ${new Date(value).toLocaleDateString("en-GB")}`;
    }
    return value;
  };

  return (
    <div>
      <AnimatedContent>
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <PageTitle title="Tasks" />
          <div className="flex items-center gap-2 flex-wrap">
            {Object.entries(filters).map(([key, value]) =>
              value ? (
                <CustomChip key={key} label={getFilterLabel(key, value)} onRemove={() => removeFilter(key)} />
              ) : null
            )}
            <button
              onClick={() => setModalOpen(true)}
              className="bg-white w-[131px] h-[50px] text-primary flex items-center justify-center gap-6 rounded-[10px] border border-primary"
              aria-label="Open filter drawer"
            >
              <span>Filter</span>
              <FaFilter className="text-xs" />
            </button>
          </div>
        </div>

        <TaskTable
          columns={columns}
          loading={loading}
          data={tasks}
          limit={limit}
          currentPage={currentPage}
          nextId={nextId}
          prevId={prevId}
          handlePageChange={handlePageChange}
          handlePageSize={handlePageSize}
          hasMore={hasMore}
        />
      </AnimatedContent>

      <Drawer open={modalOpen} title="Filters" onClose={() => setModalOpen(false)}>
        <TaskFilter
          presonList={presonList}
          filters={filters}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
        />
      </Drawer>

      <Drawer open={visible} title="Task Info" onClose={() => setVisible(false)}>
        <TaskInfoTabs task={selectedTask} defaultTab={defaultTab} />
      </Drawer>
    </div>
  );
};

export default TaskList;
